import type {
  NewsArticle,
  NewsLocale,
  NewsOverrides,
  NewsTranslations,
} from './types';

export type NewsSite = 'metown' | 'evova';

const PROJECT_ID = 'metowndb';
const RUN_QUERY_URL = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents:runQuery`;
const LOCALES: NewsLocale[] = ['ko', 'en'];
const MAX_TITLE_LENGTH = 200;
const MAX_CONTENT_LENGTH = 15_000;
const MAX_TAG_COUNT = 20;
const MAX_THUMBNAIL_COUNT = 1;

type FirestoreValue = {
  stringValue?: string;
  integerValue?: string;
  doubleValue?: number;
  booleanValue?: boolean;
  arrayValue?: { values?: FirestoreValue[] };
  mapValue?: { fields?: Record<string, FirestoreValue> };
};

function toPlain(value: FirestoreValue | undefined): unknown {
  if (!value) return undefined;
  if (value.stringValue !== undefined) return value.stringValue;
  if (value.integerValue !== undefined) return Number(value.integerValue);
  if (value.doubleValue !== undefined) return value.doubleValue;
  if (value.booleanValue !== undefined) return value.booleanValue;
  if (value.arrayValue !== undefined) {
    return (value.arrayValue.values ?? []).map(toPlain);
  }
  if (value.mapValue !== undefined) {
    const result: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value.mapValue.fields ?? {})) {
      result[key] = toPlain(nested);
    }
    return result;
  }
  return undefined;
}

type RemoteArticle = {
  articleId: number;
  date: string;
  thumbnail: string[];
  tag: string[];
  sites: string[];
  translations: NewsTranslations;
};

function parseDocument(
  fields: Record<string, FirestoreValue>,
): RemoteArticle | null {
  const articleId = toPlain(fields.articleId);
  const date = toPlain(fields.date);
  if (
    typeof articleId !== 'number' ||
    !Number.isSafeInteger(articleId) ||
    articleId <= 0 ||
    typeof date !== 'string' ||
    !isValidDate(date)
  ) {
    return null;
  }
  const asStringArray = (value: unknown, limit: number): string[] =>
    Array.isArray(value)
      ? value.filter((v): v is string => typeof v === 'string').slice(0, limit)
      : [];

  const rawTranslations = toPlain(fields.translations);
  const translations: NewsTranslations = {};
  if (rawTranslations && typeof rawTranslations === 'object') {
    for (const locale of LOCALES) {
      const entry = (rawTranslations as Record<string, unknown>)[locale];
      if (entry && typeof entry === 'object') {
        const { title, content } = entry as Record<string, unknown>;
        const normalizedTitle =
          typeof title === 'string'
            ? title.trim().slice(0, MAX_TITLE_LENGTH)
            : '';
        const normalizedContent =
          typeof content === 'string'
            ? content.slice(0, MAX_CONTENT_LENGTH)
            : '';
        if (normalizedTitle || normalizedContent) {
          translations[locale] = {
            title: normalizedTitle,
            content: normalizedContent,
          };
        }
      }
    }
  }

  return {
    articleId,
    date,
    thumbnail: asStringArray(
      toPlain(fields.thumbnail),
      MAX_THUMBNAIL_COUNT,
    ).filter(isSafePublicImageUrl),
    tag: asStringArray(toPlain(fields.tag), MAX_TAG_COUNT),
    sites: asStringArray(toPlain(fields.sites), 2).filter(
      site => site === 'metown' || site === 'evova',
    ),
    translations,
  };
}

function isValidDate(value: string) {
  const match = /^(\d{4})\.(\d{2})\.(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function isSafePublicImageUrl(value: string) {
  if (value.length > 2_048) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:';
  } catch {
    return value.startsWith('/');
  }
}

function toLocaleArticle(
  article: RemoteArticle,
  locale: NewsLocale,
): NewsArticle | null {
  const localized = article.translations[locale];
  const fallback =
    article.translations.ko ?? article.translations.en ?? localized ?? null;
  if (!localized && !fallback) return null;
  const title = localized?.title || fallback?.title || '';
  const content = localized?.content || fallback?.content || '';
  if (!title) return null;
  return {
    id: article.articleId,
    date: article.date,
    thumbnail: article.thumbnail,
    tag: article.tag,
    title,
    content,
  };
}

/**
 * Firestore `newsArticles` 컬렉션에서 게시된 기사를 읽어
 * getNewsData()의 overrides 형태로 반환한다.
 *
 * SDK 없이 공개 REST 읽기를 사용하므로 브라우저와 서버(RSS,
 * generateMetadata) 어디서든 호출 가능하다. 실패 시 빈 overrides를
 * 반환해 정적 기본 기사만으로도 페이지가 렌더링되게 한다.
 */
export async function fetchRemoteNewsOverrides(
  site: NewsSite,
  options: { revalidateSeconds?: number } = {},
): Promise<NewsOverrides> {
  const empty: NewsOverrides = { ko: [], en: [] };

  try {
    const response = await fetch(RUN_QUERY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        structuredQuery: {
          from: [{ collectionId: 'newsArticles' }],
          where: {
            fieldFilter: {
              field: { fieldPath: 'isPublished' },
              op: 'EQUAL',
              value: { booleanValue: true },
            },
          },
          orderBy: [
            {
              field: { fieldPath: '__name__' },
              direction: 'DESCENDING',
            },
          ],
          limit: 100,
        },
      }),
      ...(options.revalidateSeconds !== undefined
        ? { next: { revalidate: options.revalidateSeconds } }
        : {}),
    });

    if (!response.ok) {
      console.error('[metownlib] remote news query failed', response.status);
      return empty;
    }

    const rows: { document?: { fields?: Record<string, FirestoreValue> } }[] =
      await response.json();

    const articles = rows
      .map(row => row.document?.fields)
      .filter((fields): fields is Record<string, FirestoreValue> =>
        Boolean(fields),
      )
      .map(parseDocument)
      .filter((article): article is RemoteArticle => article !== null)
      .filter(article => article.sites.includes(site));

    return {
      ko: articles
        .map(article => toLocaleArticle(article, 'ko'))
        .filter((a): a is NewsArticle => a !== null),
      en: articles
        .map(article => toLocaleArticle(article, 'en'))
        .filter((a): a is NewsArticle => a !== null),
    };
  } catch (error) {
    console.error('[metownlib] failed to fetch remote news', error);
    return empty;
  }
}
